import { useState, useEffect, useCallback, useRef } from 'react';
import './MyDisplaysPage.scss';
import { BigDeviceList } from '../../components/BigDeviceList/BigDeviceList';
import { PersonalMediaLibraryPopup } from '../../components/PersonalMediaLibraryPopup/PersonalMediaLibraryPopup';
import { BigDisplay, DEFAULT_PLAYBACK_SETTINGS, PlaybackSettings } from '../../types/BigDisplay';
import {
  getMyDisplays,
  addDisplay,
  deleteDisplay,
  selectImageForDisplay,
  setDisplayOrientation,
  updateDisplaySettings,
  restartDisplay,
  checkDisplayOnline,
  CheckDisplayOnlineResponse,
  getDisplayTypes,
  DisplayTypeOption,
  MAX_DISPLAY_IMAGES_PER_SEND,
} from '../../services/bigDisplayData';

type OperationToastStatus = 'loading' | 'success' | 'error';

interface OperationToast {
  status: OperationToastStatus;
  message: string;
}

interface OnlineCheckState {
  status: 'checking' | 'success' | 'error';
  displayId: number;
  result?: CheckDisplayOnlineResponse;
  error?: string;
}

export const MyDisplaysPage = () => {
  const [displays, setDisplays] = useState<BigDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isAdding, setIsAdding] = useState(false);
  const [newAndroidId, setNewAndroidId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [displayTypes, setDisplayTypes] = useState<DisplayTypeOption[]>([]);
  const [selectedDisplayTypeId, setSelectedDisplayTypeId] = useState<number | ''>('');

  const [showMediaLibraryPopup, setShowMediaLibraryPopup] = useState(false);
  const [selectedDisplayId, setSelectedDisplayId] = useState<number | null>(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [operationToast, setOperationToast] = useState<OperationToast | null>(null);
  const [onlineCheck, setOnlineCheck] = useState<OnlineCheckState | null>(null);
  const toastTimeoutRef = useRef<number | null>(null);

  const showOperationToast = useCallback((status: OperationToastStatus, message: string) => {
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }

    setOperationToast({ status, message });

    if (status !== 'loading') {
      toastTimeoutRef.current = window.setTimeout(() => {
        setOperationToast(null);
        toastTimeoutRef.current = null;
      }, 4000);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const fetchDisplays = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
        setError(null);
      }
      const data = await getMyDisplays();
      setDisplays(data);
    } catch {
      if (showLoading) {
        setError('Failed to load your displays.');
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchDisplays();
    getDisplayTypes().then(setDisplayTypes).catch(() => {});
  }, [fetchDisplays]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAndroidId.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    showOperationToast('loading', 'Adding display...');
    try {
      await addDisplay(newAndroidId.trim(), selectedDisplayTypeId as number);
      setNewAndroidId('');
      setSelectedDisplayTypeId('');
      setIsAdding(false);
      await fetchDisplays();
      showOperationToast('success', 'Display added');
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to add display.');
      showOperationToast('error', 'Failed to add display');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (displayId: number) => {
    if (!window.confirm('Remove this display from your account?')) return;
    showOperationToast('loading', 'Removing display...');
    try {
      await deleteDisplay(displayId);
      setDisplays(prev => prev.filter(d => d.id !== displayId));
      showOperationToast('success', 'Display removed');
    } catch {
      showOperationToast('error', 'Failed to delete display');
    }
  };

  const handleUploadClick = (displayId: number) => {
    setSelectedDisplayId(displayId);
    setShowMediaLibraryPopup(true);
  };

  const handleMediaSelect = async (mediaUrls: string[]) => {
    if (!selectedDisplayId) return;
    if (mediaUrls.length > MAX_DISPLAY_IMAGES_PER_SEND) {
      showOperationToast('error', `You can send up to ${MAX_DISPLAY_IMAGES_PER_SEND} images at once`);
      return;
    }

    setIsUploadingMedia(true);
    showOperationToast('loading', 'Sending media to display...');
    try {
      const updated = await selectImageForDisplay(selectedDisplayId, mediaUrls);
      setDisplays(prev =>
        prev.map(d => d.id === selectedDisplayId ? {
          ...d,
          current_image: mediaUrls,
          playback_settings: {
            ...DEFAULT_PLAYBACK_SETTINGS,
            ...(d.playback_settings ?? {}),
            ...(updated.playback_settings ?? {}),
          },
        } : d)
      );
      showOperationToast('success', 'Media sent to display');
    } catch {
      showOperationToast('error', 'Failed to send media to display');
    } finally {
      setIsUploadingMedia(false);
      setShowMediaLibraryPopup(false);
    }
  };

  const handleOrientationChange = async (displayId: number, orientation: 'portrait' | 'landscape') => {
    showOperationToast('loading', 'Updating orientation...');
    try {
      await setDisplayOrientation(displayId, orientation);
      setDisplays(prev =>
        prev.map(d => d.id === displayId ? { ...d, orientation } : d)
      );
      void fetchDisplays(false);
      showOperationToast('success', 'Orientation updated');
    } catch {
      showOperationToast('error', 'Failed to update orientation');
    }
  };

  const handlePausePlay = async (displayId: number, paused: boolean) => {
    showOperationToast('loading', paused ? 'Pausing display...' : 'Resuming display...');
    try {
      const updated = await updateDisplaySettings(displayId, { paused });
      setDisplays(prev =>
        prev.map(d => d.id === displayId
          ? { ...d, playback_settings: { ...DEFAULT_PLAYBACK_SETTINGS, ...updated } }
          : d
        )
      );
      void fetchDisplays(false);
      showOperationToast('success', paused ? 'Display paused' : 'Display resumed');
    } catch {
      showOperationToast('error', 'Failed to update display.');
    }
  };

  const handleRestart = async (displayId: number) => {
    showOperationToast('loading', 'Sending restart command...');
    try {
      await restartDisplay(displayId);
      setDisplays(prev =>
        prev.map(d => d.id === displayId
          ? {
              ...d,
              playback_settings: {
                ...DEFAULT_PLAYBACK_SETTINGS,
                ...(d.playback_settings ?? {}),
                paused: false,
              },
            }
          : d
        )
      );
      void fetchDisplays(false);
      showOperationToast('success', 'Restart command sent');
    } catch {
      showOperationToast('error', 'Failed to restart display');
    }
  };

  const handleCheckOnline = async (displayId: number) => {
    setOnlineCheck({ status: 'checking', displayId });
    try {
      const result = await checkDisplayOnline(displayId);
      if (result.online) {
        setDisplays(prev =>
          prev.map(d => d.id === displayId
            ? {
                ...d,
                battery_level: result.battery_level,
                wifi_rssi: result.wifi_rssi,
                battery_updated_at: result.battery_updated_at,
              }
            : d
          )
        );
      }
      setOnlineCheck({ status: 'success', displayId, result });
    } catch (err: any) {
      setOnlineCheck({
        status: 'error',
        displayId,
        error: err.message || 'Failed to check display status',
      });
    }
  };

  const handleSettingsChange = async (displayId: number, patch: Partial<PlaybackSettings>) => {
    showOperationToast('loading', 'Saving display settings...');
    try {
      const updated = await updateDisplaySettings(displayId, patch);
      setDisplays(prev =>
        prev.map(d => d.id === displayId ? { ...d, playback_settings: { ...DEFAULT_PLAYBACK_SETTINGS, ...updated } } : d)
      );
      void fetchDisplays(false);
      showOperationToast('success', 'Display settings saved');
    } catch {
      showOperationToast('error', 'Failed to save display settings');
    }
  };

  return (
    <div className="my-displays-page">
      <div className="my-displays-page__header">
        <p className="my-displays-page__title">My Displays</p>
        <button
          className="my-displays-page__add-btn"
          onClick={() => { setIsAdding(v => !v); setSubmitError(null); }}
        >
          {isAdding ? 'Cancel' : '+ Add Display'}
        </button>
      </div>

      {operationToast && (
        <div
          className={`my-displays-page__toast my-displays-page__toast--${operationToast.status}`}
          role="status"
          aria-live="polite"
        >
          <span className="my-displays-page__toast-indicator" />
          <span>{operationToast.message}</span>
        </div>
      )}

      {isAdding && (
        <form className="my-displays-page__form" onSubmit={handleAdd}>
          <div className="my-displays-page__form-field">
            <label className="my-displays-page__form-label">Mac Address</label>
            <input
              className="my-displays-page__form-input"
              type="text"
              placeholder="e.g. 12CDE5A3DC26"
              value={newAndroidId}
              onChange={e => setNewAndroidId(e.target.value)}
              required
            />
          </div>
          <div className="my-displays-page__form-field">
            <label className="my-displays-page__form-label">Display Type</label>
            <select
              className="my-displays-page__form-input"
              value={selectedDisplayTypeId}
              onChange={e => setSelectedDisplayTypeId(Number(e.target.value))}
              required
            >
              <option value="" disabled>Select a display type</option>
              {displayTypes.map(dt => (
                <option key={dt.id} value={dt.id}>
                  {dt.name} ({dt.width}×{dt.height})
                </option>
              ))}
            </select>
          </div>
          {submitError && <p className="my-displays-page__error">{submitError}</p>}
          <button
            className="my-displays-page__submit-btn"
            type="submit"
            disabled={submitting}
          >
            {submitting ? 'Adding...' : 'Add Display'}
          </button>
        </form>
      )}

      {loading && <p className="my-displays-page__status">Loading displays...</p>}
      {error && <p className="my-displays-page__error">{error}</p>}

      {!loading && !error && displays.length === 0 && (
        <p className="my-displays-page__status">You have no displays yet. Add one above.</p>
      )}

      {!loading && displays.length > 0 && (
        <BigDeviceList
          displays={displays}
          onUploadClick={handleUploadClick}
          onOrientationChange={handleOrientationChange}
          onDeleteClick={handleDelete}
          onPausePlay={handlePausePlay}
          onRestart={handleRestart}
          onCheckOnline={handleCheckOnline}
          checkingOnlineId={onlineCheck?.status === 'checking' ? onlineCheck.displayId : null}
          onSettingsChange={handleSettingsChange}
          defaultColumns={3}
        />
      )}

      {onlineCheck && (
        <div className="my-displays-page__status-overlay">
          <div className="my-displays-page__status-dialog">
            <div className="my-displays-page__status-dialog-header">
              <h2>Display Status</h2>
              <button
                type="button"
                onClick={() => setOnlineCheck(null)}
                disabled={onlineCheck.status === 'checking'}
              >
                ×
              </button>
            </div>

            {onlineCheck.status === 'checking' && (
              <div className="my-displays-page__status-dialog-body">
                <span className="my-displays-page__status-spinner" />
                <p>Checking display online status...</p>
              </div>
            )}

            {onlineCheck.status === 'error' && (
              <div className="my-displays-page__status-dialog-body">
                <p className="my-displays-page__status-result my-displays-page__status-result--offline">
                  Could not check display
                </p>
                <p>{onlineCheck.error}</p>
              </div>
            )}

            {onlineCheck.status === 'success' && onlineCheck.result && (
              <div className="my-displays-page__status-dialog-body">
                <p className={`my-displays-page__status-result ${onlineCheck.result.online ? 'my-displays-page__status-result--online' : 'my-displays-page__status-result--offline'}`}>
                  {onlineCheck.result.online ? 'Online' : 'Offline'}
                </p>
                {!onlineCheck.result.online && (
                  <p>No battery or WiFi response was received.</p>
                )}
                <dl className="my-displays-page__status-details">
                  <div>
                    <dt>MAC</dt>
                    <dd>{onlineCheck.result.mac}</dd>
                  </div>
                  {onlineCheck.result.online && (
                    <>
                      <div>
                        <dt>Battery</dt>
                        <dd>{onlineCheck.result.battery_level != null ? `${onlineCheck.result.battery_level}%` : 'Unknown'}</dd>
                      </div>
                      <div>
                        <dt>WiFi</dt>
                        <dd>{onlineCheck.result.wifi_rssi != null ? `${onlineCheck.result.wifi_rssi} dBm` : 'Unknown'}</dd>
                      </div>
                    </>
                  )}
                  <div>
                    <dt>Reason</dt>
                    <dd>{onlineCheck.result.message || onlineCheck.result.reason || 'Response received'}</dd>
                  </div>
                </dl>
              </div>
            )}
          </div>
        </div>
      )}

      {showMediaLibraryPopup && (
        <div className="my-displays-page__popup-overlay">
          <PersonalMediaLibraryPopup
            onClose={() => setShowMediaLibraryPopup(false)}
            onMediaSelect={handleMediaSelect}
            isUploading={isUploadingMedia}
            displayId={selectedDisplayId ?? undefined}
            onStatus={showOperationToast}
            maxSelection={MAX_DISPLAY_IMAGES_PER_SEND}
          />
        </div>
      )}
    </div>
  );
};
